import DeleteIcon from "@mui/icons-material/Delete"
import NavigateNextIcon from "@mui/icons-material/NavigateNext"
import WarningIcon from "@mui/icons-material/Warning"
import { IconButton, Stack, Typography, useTheme } from "@mui/material"
import { useConfirm } from "material-ui-confirm"
import { ReactElement } from "react"
import { useNavigate } from "react-router-dom"
import { KeyedMutator } from "swr"

import { AvatarContentCell } from "./installerDraftTable"
import {
    TT_CONTINUE_BUTTON,
    TT_DELETE,
    TT_WARNING_DELETE_DRAFT,
} from "../../../constants/tooltips"
import { NEW_SINGLE_APPLICATION } from "../../../router/routes"
import useDeleteDraft from "../../../services/middleware/hooks/draftApplication/useDeleteDraft"
import {
    IDraftApplication,
    IDraftApplicationListResponse,
} from "../../../services/middleware/types/installerInterface/installerApplicationTypes"
import { formatDate } from "../../../utils/general/generalUtils"
import AppButton from "../../buttons/appButton"
import CopyTypography from "../../dataDisplay/typography/copyTypography"
import AppTooltip from "../../helper/appTooltip"
import LctChip from "../../pills/lct/lctChip"
import AppTableCell from "../appTableCell"
import AppTableContentRow from "../appTableContentRow"

const ADDRESS_MAX_LENGTH = 25

interface IApplicationRowProps {
    draftApplication: IDraftApplication
    mutate: KeyedMutator<IDraftApplicationListResponse[]>
}
/**
 * Single row in my InstallerDraftTable for Installers to view their draft applications.
 * Includes logic for continuing and deleting draft applications.
 */
const InstallerDraftRow = ({
    draftApplication,
    mutate,
}: IApplicationRowProps): ReactElement => {
    const theme = useTheme()
    const confirm = useConfirm()
    const navigate = useNavigate()

    const addressAsString = String(draftApplication.mainAddress) ?? ""

    // Calculate if the draft is 5 months old
    const isFiveMonthsOld = (): boolean => {
        const lastUpdatedDate = new Date(draftApplication.lastUpdated)
        const currentDate = new Date()
        const fiveMonthsAgo = new Date()
        fiveMonthsAgo.setMonth(currentDate.getMonth() - 5)
        return lastUpdatedDate <= fiveMonthsAgo
    }
    const { deleteDraft } = useDeleteDraft(draftApplication.draftId, mutate)

    // Display a confirmation dialog before deleting a draft application
    const deleteConformation = (): void => {
        confirm({
            title: (
                <Typography variant="h6" component="div" align="center">
                    Are you sure?
                </Typography>
            ),
            description: (
                <Stack direction="row" component="span">
                    <WarningIcon
                        sx={{
                            fontSize: "2.5rem",
                            color: theme.palette.warning.main,
                            verticalAlign: "middle",
                            marginRight: "1rem",
                        }}
                    />
                    <Typography variant="body1" component="span">
                        Draft Application {draftApplication.draftId} will be
                        permanently deleted and removed
                    </Typography>
                </Stack>
            ),
        })
            .then(() => {
                deleteDraft()
            })
            .catch(() => {
                /* ... */
            })
    }

    const addressTruncated =
        addressAsString.length > ADDRESS_MAX_LENGTH
            ? `${addressAsString.substring(0, ADDRESS_MAX_LENGTH)}...`
            : addressAsString

    return (
        <AppTableContentRow
            key={draftApplication.draftId}
            hover
            sx={{
                cursor: "pointer",
                backgroundColor: isFiveMonthsOld()
                    ? theme.palette.error.light
                    : "inherit",
            }}
        >
            {isFiveMonthsOld() ? (
                <AppTableCell align="center">
                    <AppTooltip title={TT_WARNING_DELETE_DRAFT}>
                        <WarningIcon
                            sx={{
                                fontSize: "1.5rem",
                                color: theme.palette.warning.main,
                            }}
                        />
                    </AppTooltip>
                </AppTableCell>
            ) : (
                <AppTableCell />
            )}
            <AvatarContentCell lcts={draftApplication.lcts} />

            <AppTableCell>
                <CopyTypography
                    text={addressTruncated}
                    customCopyText={addressAsString}
                    typographyProps={{ variant: "body2" }}
                    customTooltip={`${addressAsString} - click to copy to clipboard!`}
                />
            </AppTableCell>
            <AppTableCell>
                <CopyTypography
                    text={draftApplication.postCode}
                    customCopyText={draftApplication.postCode}
                    typographyProps={{ variant: "body2" }}
                    customTooltip={`${draftApplication.postCode} - click to copy to clipboard!`}
                />
            </AppTableCell>
            <AppTableCell>
                <CopyTypography
                    typographyProps={{ variant: "body2" }}
                    customCopyText={draftApplication.draftId}
                    text={`${draftApplication.draftId.substring(0, 17)}...`}
                    customTooltip={`${draftApplication.draftId} - click to copy to clipboard!`}
                />
            </AppTableCell>
            <AppTableCell>
                {formatDate(draftApplication.lastUpdated)}
            </AppTableCell>
            <AppTableCell align="center">
                {draftApplication.completionStatus}%
            </AppTableCell>
            {draftApplication.lcts !== null && (
                <AppTableCell>
                    {draftApplication.lcts.map((lct) => (
                        <LctChip
                            key={lct}
                            lct={lct}
                            sx={{
                                mr: 1,
                                mb: draftApplication.lcts.length > 0 ? 1 : 0,
                            }}
                        />
                    ))}
                </AppTableCell>
            )}

            <AppTableCell>
                <AppTooltip title={TT_CONTINUE_BUTTON}>
                    <AppButton
                        endIcon={<NavigateNextIcon />}
                        variant="outlined"
                        color="primary"
                        onClick={(e) => {
                            navigate(
                                `${NEW_SINGLE_APPLICATION}?draftId=${draftApplication.draftId}`
                            )
                        }}
                    >
                        Continue
                    </AppButton>
                </AppTooltip>
            </AppTableCell>
            <AppTableCell align="left" sx={{ verticalAlign: "middle" }}>
                <AppTooltip title={TT_DELETE}>
                    <IconButton
                        color="error"
                        onClick={deleteConformation}
                        aria-label={TT_DELETE}
                    >
                        <DeleteIcon />
                    </IconButton>
                </AppTooltip>
            </AppTableCell>
        </AppTableContentRow>
    )
}

export default InstallerDraftRow
